import React, { createContext, useEffect, useState, useContext, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { apiConfig } from "Services/config";
import { Paths } from "Router/Paths";
import { PATH, ROLE } from "Helpers/ats.constants";
import { ApiService } from "Services/ApiService";
import { menuList } from "Static/utils/menuList";

// Match the backend access-token TTL — 30 min of no user input → logout.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const UserContext = createContext();

const useUserContext = () => {
  return useContext(UserContext);
};

const UserContextProvider = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [userDetails, setuserDetails] = useState(null);
  const [serverDown, setServerDown] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [permission, setPermission] = useState([]);
  const [loader, setLoader] = useState(true);
  const [filteredMenuList, setFilteredMenuList] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  const apiServiceClass = new ApiService(`${apiConfig.backendUrl}`);

  const loadAppConfig = useCallback(async () => {
    try {
      const response = await apiServiceClass.getAppConfig();
      if (response?.success && response?.data) {
        const userData = response.data;
        setuserDetails(userData);
        setPermission(userData.module_permissions || []);

        // Filter menu based on user's module permissions + admin role check
        const userModuleSlugs = (userData.module_permissions || []).map((m) => m.module_slug);
        const isAdmin = [ROLE.SUPER_ADMIN, ROLE.ADMIN].includes(userData.role_slug);
        const filtered = menuList.filter((item) => {
          // Items with a module_slug are gated by that permission; items
          // without one (e.g. Audit Trail) are gated purely by adminOnly.
          if (item.module_slug && !userModuleSlugs.includes(item.module_slug)) return false;
          if (item.adminOnly && !isAdmin) return false;
          return true;
        });
        setFilteredMenuList(filtered);
        setLoggedIn(true);
      }
    } catch (error) {
      console.error("Failed to load app config:", error);
      localStorage.clear();
      setLoggedIn(false);
      setuserDetails(null);
      navigate(Paths.base);
    } finally {
      setLoader(false);
    }
  }, []);

  const roleBasedRedirect = async (auth) => {
    try {
      if (auth) {
        if (
          location.pathname === `/${PATH}` ||
          location.pathname === `/${PATH}/` ||
          location.pathname === "/"
        ) {
          navigate(Paths.dashboard, { replace: true });
        }
      } else {
        if (
          location.pathname !== "/" &&
          location.pathname.startsWith(`/${PATH}`) &&
          !auth
        ) {
          if (localStorage.getItem("user_session")) {
            await apiServiceClass.getLogoutUser();
          }
          setuserDetails(null);
          setLoggedIn(false);
          navigate(Paths.base);
          localStorage.clear();
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    try {
      const auth = localStorage.getItem("user_session") === "true";
      roleBasedRedirect(auth);
    } catch (error) {
      console.error(error);
    }
  }, [location]);

  // On initial load, check session and load app config
  useEffect(() => {
    try {
      const auth = localStorage.getItem("user_session") === "true";
      if (auth) {
        loadAppConfig();
      } else {
        setuserDetails(null);
        setLoggedIn(false);
        setLoader(false);
        navigate(Paths.base);
        localStorage.clear();
      }
    } catch (error) {
      console.error(error);
      setLoader(false);
    }
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Idle watchdog ────────────────────────────────────────────────
  // Logs the user out after IDLE_TIMEOUT_MS of no user input. Resets
  // the timer on mouse / keyboard / touch / scroll. The backend
  // access token also expires at 30 min — this watchdog forces a
  // visible logout instead of silently letting the refresh token
  // revive an idle session.
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (!loggedIn) return undefined;

    const handleIdle = async () => {
      try {
        if (localStorage.getItem("user_session")) {
          await apiServiceClass.getLogoutUser().catch(() => {});
        }
      } finally {
        setuserDetails(null);
        setLoggedIn(false);
        localStorage.clear();
        message.info("You were logged out after 30 minutes of inactivity.");
        navigate(Paths.base);
      }
    };

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(handleIdle, IDLE_TIMEOUT_MS);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    // apiServiceClass is recreated per render but only its methods are
    // closure-captured here — safe to depend solely on loggedIn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  return (
    <UserContext.Provider
      value={{
        loggedIn,
        userDetails,
        serverDown,
        setServerDown,
        setLoggedIn,
        setuserDetails,
        permission,
        setPermission,
        windowWidth,
        setWindowWidth,
        loader,
        setLoader,
        filteredMenuList,
        setFilteredMenuList,
        loadAppConfig,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { useUserContext, UserContextProvider };
